import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { useTheme } from '../../../src/theme/useTheme';
import { getWcColors } from './matchPickStats.helper';
import { getPickStatsOutcomePieStyles } from './PickStatsOutcomePie.styles';
import { getMatchPickStatsStyles } from './MatchPickStatsScreen.styles';
import { MATCH_PICK_STATS_COPY } from './MatchPickStats.constants';
import type { TeamKitConfig } from '../../../constants/teamKitColors';
import { PickStatsTeamCrest } from './PickStatsTeamCrest';

interface PickStatsOutcomePieProps {
  home: number;
  draw: number;
  away: number;
  homeCountryCode: string | null;
  awayCountryCode: string | null;
  /** Kit mode (AC-10.6): non-null for a club competition, null for a national-team one. */
  homeKit?: TeamKitConfig | null;
  awayKit?: TeamKitConfig | null;
}

const SIZE = 140;
const CX = 70;
const CY = 70;
const R = 60;
const HOLE_R = 32;
const GAP = 0.035;

function polarToXY(r: number, angle: number) {
  return { x: CX + r * Math.cos(angle), y: CY + r * Math.sin(angle) };
}

function arcPath(r: number, startAngle: number, endAngle: number, holeR: number): string {
  const os = polarToXY(r, startAngle);
  const oe = polarToXY(r, endAngle);
  const is = polarToXY(holeR, endAngle);
  const ie = polarToXY(holeR, startAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${os.x} ${os.y}`,
    `A ${r} ${r} 0 ${large} 1 ${oe.x} ${oe.y}`,
    `L ${is.x} ${is.y}`,
    `A ${holeR} ${holeR} 0 ${large} 0 ${ie.x} ${ie.y}`,
    'Z',
  ].join(' ');
}

function fmtPct(v: number) { return `${Math.round(v * 100)}%`; }

export const PickStatsOutcomePie: React.FC<PickStatsOutcomePieProps> = ({
  home, draw, away, homeCountryCode, awayCountryCode, homeKit = null, awayKit = null,
}) => {
  const theme = useTheme();
  const C = useMemo(() => getWcColors(theme), [theme]);
  const shared = useMemo(() => getMatchPickStatsStyles(C), [C]);
  const pie = useMemo(() => getPickStatsOutcomePieStyles(C), [C]);

  const segments = [
    { key: 'home' as const, label: MATCH_PICK_STATS_COPY.OUTCOME_HOME, value: home, color: C.home, countryCode: homeCountryCode, kit: homeKit },
    { key: 'draw' as const, label: MATCH_PICK_STATS_COPY.OUTCOME_DRAW, value: draw, color: C.draw, countryCode: null, kit: null },
    { key: 'away' as const, label: MATCH_PICK_STATS_COPY.OUTCOME_AWAY, value: away, color: C.away, countryCode: awayCountryCode, kit: awayKit },
  ];
  const dominant = segments.reduce((b, s) => (s.value > b.value ? s : b), segments[0]);

  let angle = -Math.PI / 2;
  const paths = segments.map((seg) => {
    const sweep = seg.value * 2 * Math.PI - GAP;
    const start = angle + GAP / 2;
    const end = start + Math.max(sweep, 0.001);
    angle += seg.value * 2 * Math.PI;
    return { ...seg, path: arcPath(R, start, end, HOLE_R) };
  });

  return (
    <View style={shared.panelCard}>
      <View style={pie.pieInner}>
        <View style={pie.pieChartWrap}>
          <Svg width={SIZE} height={SIZE}>
            {paths.map((seg) => (
              <Path key={seg.key} d={seg.path} fill={seg.color} />
            ))}
            <Circle cx={CX} cy={CY} r={HOLE_R - 2} fill={C.panel} />
          </Svg>
          <View style={[pie.pieCenterLabel, pie.pieCenterLabelPointerEvents]}>
            <Text style={[pie.pieCenterPct, { color: dominant.color }]}>
              {fmtPct(dominant.value)}
            </Text>
            <Text style={pie.pieCenterKey} numberOfLines={1}>
              {dominant.label}
            </Text>
          </View>
        </View>
        <View style={pie.pieLegend}>
          {segments.map((seg) => (
            <View key={seg.key} style={pie.pieLegendRow}>
              <View style={[pie.pieLegendDot, { backgroundColor: seg.color }]} />
              {seg.countryCode || seg.kit ? (
                <PickStatsTeamCrest
                  countryCode={seg.countryCode}
                  kit={seg.kit}
                  size="mini"
                  accessibilityLabel={seg.label}
                />
              ) : (
                <Text style={pie.pieLegendLabel}>{seg.label}</Text>
              )}
              <Text style={[pie.pieLegendPct, { color: seg.color }]}>
                {fmtPct(seg.value)}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};
